import { FontAwesome5 } from '@expo/vector-icons';
import { styled } from '@openeducation/oe-rn-themes';
import { Picker } from '@react-native-picker/picker';
import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Platform } from 'react-native';
import { TextInput, useTheme } from 'react-native-paper';

import { Loading } from '../../atoms';
import { flex } from '../../style-utils';
import { SelectInputButton } from '../select-input/select-input-button';
import { ModalPickerContent } from './modal-picker-content';

export type PickerItem = {
  title: string;
  value: string;
};

type PaperTextInputProps = React.ComponentProps<typeof TextInput>;

export type InputPickerProps = Partial<PaperTextInputProps> & {
  items: PickerItem[];
  onChange: (value: string) => void;
  loading?: boolean;
  title?: string;
  label: string;
  defaultItemValue?: string;
  pickerMode?: 'dialog' | 'dropdown';
};

export function InputPicker({
  items,
  onChange,
  loading,
  title,
  autoComplete,
  label,
  defaultItemValue,
  pickerMode,
  ...props
}: InputPickerProps) {
  const { colors } = useTheme();
  const [visible, setVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(() =>
    items.find(({ value }) => value === defaultItemValue)
  );
  const pickerRef = useRef<Picker<string>>(null);

  useEffect(() => {
    if (defaultItemValue) {
      setSelectedItem(items.find(({ value }) => value === defaultItemValue));
    }
  }, [defaultItemValue, items]);

  useEffect(() => {
    if (visible) {
      pickerRef.current?.focus();
    }
  }, [visible]);

  const handleOnChange = (value: string) => {
    onChange(value);
    setVisible(false);
  };

  return (
    <>
      <TextInput
        autoComplete={autoComplete}
        value={selectedItem ? selectedItem?.title : undefined}
        label={label}
        {...props}
        render={() =>
          loading ? (
            <StyledLoading />
          ) : (
            <SelectInputButton
              placeholder={label}
              title={selectedItem?.title}
              onPress={() => {
                Keyboard.dismiss();
                setVisible(true);
              }}
              icon={
                <FontAwesome5
                  name="caret-down"
                  size={16}
                  color={colors.dark1}
                />
              }
            />
          )
        }
      />

      {['ios', 'web'].includes(Platform.OS) ? (
        <ModalPickerContent
          visible={visible}
          onClose={() => setVisible(false)}
          value={selectedItem?.value}
          items={items}
          onChange={handleOnChange}
          title={title}
          mode={pickerMode}
        />
      ) : (
        <HiddenPickerContainer>
          <Picker
            ref={pickerRef}
            selectedValue={selectedItem?.value}
            onValueChange={(itemValue) => {
              onChange(itemValue);
              setVisible(false);
            }}
            style={flex('flex-1')}
            itemStyle={flex('flex-1')}
            mode={pickerMode}
          >
            {items.map((item, i) => (
              <Picker.Item key={i} value={item.value} label={item.title} />
            ))}
          </Picker>
        </HiddenPickerContainer>
      )}
    </>
  );
}

const StyledLoading = styled(Loading).attrs(({ theme }) => ({
  color: theme.color.primary,
}))`
  flex: 1;
  align-items: flex-end;
  justify-content: center;
  padding: 0 16px;
`;

const HiddenPickerContainer = styled.View`
  display: none;
`;
